/**
 * EmailService — orchestrates the full email pipeline.
 *
 * Pipeline: render template → build message → enqueue →
 * dequeue → provider.send → log result → analytics/audit.
 *
 * Provider-agnostic: swaps between ConsoleEmailProvider
 * (dev) and ProductionEmailProvider (prod) via the
 * EmailProvider interface. No vendor logic in this file.
 */

import type {
  EmailTemplateName,
  EmailContext,
  EmailPriority,
  EmailMessage,
  EmailProvider,
} from './types';
import { renderTemplate, hasTemplate } from './renderer';
import { registerAllTemplates } from './templates';
import { emailQueue, type QueuedEmail } from './EmailQueue';
import { ProductionEmailProvider } from './ProductionEmailProvider';
import {
  TemplateMissingError,
  InvalidRecipientError,
  ProviderUnavailableError,
  isEmailError,
} from './errors';
import { getBrandConfig } from './brand';
import { LoggerFactory } from '@/lib/logging';
import { auditService } from '@/lib/AuditService';
import { performanceService } from '@/lib/PerformanceService';
import { globalErrorService } from '@/lib/GlobalErrorService';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let templatesRegistered = false;

class EmailServiceImpl {
  private provider: EmailProvider;
  private logger = LoggerFactory.create();
  private processing = false;

  constructor(provider?: EmailProvider) {
    this.provider = provider ?? new ProductionEmailProvider();
  }

  setProvider(provider: EmailProvider): void {
    this.provider = provider;
  }

  getProvider(): EmailProvider {
    return this.provider;
  }

  async send(
    to: string,
    templateName: EmailTemplateName,
    context: EmailContext = {},
    priority: EmailPriority = 'normal',
    options?: {
      recipientUserId?: string;
      applicationId?: string;
      attachments?: EmailMessage['attachments'];
    },
  ): Promise<QueuedEmail> {
    this.ensureTemplatesRegistered();

    if (!EMAIL_REGEX.test(to)) {
      throw new InvalidRecipientError(to);
    }
    if (!hasTemplate(templateName)) {
      throw new TemplateMissingError(templateName);
    }

    const brand = getBrandConfig();
    const rendered = performanceService.measureSync(
      'email.render',
      () => renderTemplate(templateName, context, brand),
    );

    const message: EmailMessage = {
      to,
      from: this.getSender(),
      replyTo: this.getReplyTo(),
      subject: rendered.subject,
      htmlBody: rendered.htmlBody,
      textBody: rendered.textBody,
      attachments: options?.attachments,
      metadata: {
        template: templateName,
        priority,
        recipientUserId: options?.recipientUserId,
        applicationId: options?.applicationId,
        queuedAt: new Date().toISOString(),
        retryCount: 0,
      },
    };

    const queued = emailQueue.enqueue(message);
    void this.processQueue();
    return queued;
  }

  private async processQueue(): Promise<void> {
    if (this.processing) return;
    this.processing = true;

    try {
      while (true) {
        const item = emailQueue.dequeue();
        if (!item) break;

        try {
          const available = await this.provider.isAvailable();
          if (!available) {
            emailQueue.markFailed(item.id, new ProviderUnavailableError(this.provider.name).message);
            continue;
          }

          const result = await performanceService.measure(
            'email.provider.send',
            () => this.provider.send(item.message),
            { emailId: item.id, template: item.message.metadata.template },
          );

          if (result.success) {
            emailQueue.markSent(item.id, result.messageId);
            await this.recordAudit(item);
          } else {
            emailQueue.markFailed(item.id, result.error ?? 'Unknown error');
          }
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : String(err);
          emailQueue.markFailed(item.id, errorMsg);
          if (!isEmailError(err)) {
            globalErrorService.capture(err, 'service', { operation: 'email.send', emailId: item.id });
          }
        }
      }
    } finally {
      this.processing = false;
    }
  }

  private getSender(): string {
    const sender = import.meta.env.VITE_EMAIL_SENDER;
    if (!sender || sender.trim() === '') {
      throw new Error(
        'VITE_EMAIL_SENDER is not set. Email cannot be sent without a verified sender address. ' +
        'Add VITE_EMAIL_SENDER to your .env file.',
      );
    }
    return sender;
  }

  private getReplyTo(): string {
    const replyTo = import.meta.env.VITE_EMAIL_REPLY_TO;
    if (!replyTo || replyTo.trim() === '') {
      throw new Error(
        'VITE_EMAIL_REPLY_TO is not set. Email cannot be sent without a reply-to address. ' +
        'Add VITE_EMAIL_REPLY_TO to your .env file.',
      );
    }
    return replyTo;
  }

  private async recordAudit(item: QueuedEmail): Promise<void> {
    if (!item.message.metadata.recipientUserId) return;
    try {
      await auditService.record({
        actor_id: item.message.metadata.recipientUserId,
        actor_role: 'system',
        target_type: 'email',
        target_id: item.id,
        action: 'profile_update',
        previous_value: null,
        new_value: item.message.metadata.template,
        notes: `Email sent: ${item.message.subject}`,
      });
    } catch (err) {
      this.logger.warn('[email] Audit record failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private ensureTemplatesRegistered(): void {
    if (templatesRegistered) return;
    registerAllTemplates();
    templatesRegistered = true;
  }
}

export const emailService = new EmailServiceImpl();
